// material-ui
import { Divider, Grid, makeStyles, Tab, Tabs, Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';

import { track } from '~/src/dash/analytics/analytics';
import { IUser } from '~/src/dash/api/console-api';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { spacing } from '~/src/dash/publicaUI';

// COMPONENT DESCRIPTION: Settings Tabs can be used to implement tab's to flow between various
// renders for a settings view (implemented in NetworkSettingsView).  This component
// handles the management of the tab number

// TODO: Use this component in Bidder, Site, Campaign form views
export interface ITab {
  // label for the tab
  label: string;
  // render function when the tab is selected
  render: () => React.ReactNode;
  disabled?: boolean;
  tooltipText?: string;
  // optional icon to appear on the right side of the label
  icon?: React.JSX.Element;
}

interface IProps {
  tabs: ITab[];
  // optional margin top for positioning tabs
  marginTop?: number;
  // option margin bottom for giving distance between tabs and content
  marginBottom?: number;
  paddingRight?: number;
  // optional max width of tab
  maxTabWidth?: string;
  // optional function to let parent know what tab state is being used
  setSettingsTabsCurrentTab?: (val: number) => void;
  startingTab?: number;
  // enables mix panel event tracking
  enableTrackEvents?: boolean;
  resource?: string;
  user?: IUser;
  renderDrawer?: () => JSX.Element;
  // for updating the tab when clicking outside of the SettingsTabs component
  newTab?: number;
}

const useStyles = makeStyles({
  indicator: {
    color: '#4952FF',
    backgroundColor: '#4952FF',
  },
});

export const SettingsTabs = (props: IProps) => {
  const [currentTab, setCurrentTab] = useState(0);
  const {
    tabs,
    marginTop,
    marginBottom,
    maxTabWidth,
    setSettingsTabsCurrentTab,
    enableTrackEvents,
    user,
    resource,
    paddingRight,
  } = props;
  const classes = useStyles();
  useEffect(
    () => handleChangeTab({} as React.ChangeEvent, props.startingTab || 0),
    [props.startingTab],
  );

  // this function allows for changing the tab when clicking somewhere outside of this component
  // the component rendering SettingsTabs must have a newTab in its state that is updated onClick
  // that update will trigger changing the tab in the below function
  useEffect(() => {
    if (props.newTab) {
      handleChangeTab({} as React.ChangeEvent, props.newTab);
    }
  }, [props.newTab]);

  const handleChangeTab = (_: React.ChangeEvent<{}>, newValue: number): void => {
    setCurrentTab(newValue);
    if (setSettingsTabsCurrentTab) {
      setSettingsTabsCurrentTab(newValue);
    }
  };

  const trackEvent = (label: string) => {
    if (!user || !resource) {
      return;
    }
    const is_super_user = hasRoleSuperUser(user);
    if (resource === 'Channels') {
      if (label === 'Details') {
        track(
          'Channel Settings - View Details Tab',
          {
            is_super_user,
          },
          user,
        );
      } else if (label === 'Rewrites') {
        track(
          'Channel Settings - View Rewrites Tab',
          {
            is_super_user,
          },
          user,
        );
      } else if (label === 'Brand Safety') {
        track(
          'Channel Settings - View Brand Safety Tab',
          {
            is_super_user,
          },
          user,
        );
      } else if (label === 'Endpoint') {
        track(
          'Channel Settings - View Endpoint Tab',
          {
            is_super_user,
          },
          user,
        );
      } else if (label === 'Recommendations') {
        track(
          'Channel Settings - View Recommendations Tab',
          {
            is_super_user,
          },
          user,
        );
      }
    }
  };
  const isRuleTargetingTab =
    props.tabs[currentTab] && props.tabs[currentTab].label === 'Rule Targeting';
  return (
    <Grid
      style={{
        position: 'relative',
        width: '100%',
        flexGrow: 1,
        marginTop: marginTop === undefined ? 75 : marginTop,
      }}
    >
      <Tabs
        value={currentTab}
        onChange={handleChangeTab}
        variant='scrollable'
        aria-label='simple tabs'
        classes={{ indicator: classes.indicator }}
      >
        {tabs.map((tab: ITab, index: number) => {
          if (tab.disabled) {
            return (
              <Tooltip title={tab.tooltipText || ''}>
                <span>
                  <Tab
                    label={tab.label}
                    style={{
                      color: currentTab === index ? '#4952FF' : '#757575',
                    }}
                    disabled={true}
                  />
                </span>
              </Tooltip>
            );
          }
          return (
            <Tab
              label={
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.small }}>
                  <span>{tab.label}</span>
                  {tab.icon}
                </div>
              }
              style={{
                color: currentTab === index ? '#4952FF' : '#757575',
              }}
              onClick={() => {
                if (enableTrackEvents) {
                  trackEvent(tab.label);
                }
              }}
            />
          );
        })}
      </Tabs>
      <Divider
        style={{
          width: '100%',
          marginBottom: marginBottom || 0,
        }}
      />
      {tabs.map((tab: ITab, index: number) => {
        if (currentTab !== index) return false;
        return (
          <Grid
            style={{
              maxWidth: maxTabWidth || '100%',
              height: '100%',
            }}
            role='tabpanel'
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
          >
            <Grid item style={{ paddingRight: paddingRight || '0px', height: '100%' }}>
              {tab.render()}
            </Grid>
          </Grid>
        );
      })}

      {isRuleTargetingTab && props.renderDrawer && (
        <Grid
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '290px',
            height: '100%',
          }}
        >
          {props.renderDrawer()}
        </Grid>
      )}
    </Grid>
  );
};
